/**
 * The water stream (spec 02 §8 Water), ported from rebb64 D_1B40 (run from the IRQ on I-ticks while
 * $4D is set), its start in routine_3DB0 ($3E06) and the carried sprites' state $0D
 * (spawn_position_init $284D). Eleven cells ($A74B columns, $A756 rows in C64 rows = tile row + 4):
 * each I-tick they shift one place toward the tail and the head moves one cell - falling through
 * space and through the platform it starts in, then flowing a random way, turning at walls, pouring
 * off edges, gone past the bottom. Cell 9 catches players and monsters within 12 px and carries
 * them; carried monsters die at the bottom (a yellow diamond, no chain), players are set down there.
 */

import { rngOf } from '../random';
import { DIED_IN_WATER } from './food';
import { killMonster } from './items';
import { colOf, mapSolid } from './slots';
import { systemOf } from './system';
import {
  WATER_BOTTOM_Y, WATER_CARRY_CELL, WATER_CELLS, WATER_DROP_SPLIT, WATER_DROP_X, WATER_END_ROW, WATER_RANGE,
  WATER_Y_OFFSET,
} from './tuning';
import type { Bubble, Player, PlayerId, World } from '../types';
import type { WaterState } from './state';

/**
 * $3E06: a burst water bubble starts the stream at its column + 1, row + 1 (all 11 cells there,
 * falling) - unless a stream is running or that row is past the bottom. True if it started.
 */
export function startWater(world: World, b: Bubble): boolean {
  const sys = systemOf(world);
  const row = b.motion.row + 1;
  if (sys.water || row >= WATER_END_ROW) return false;
  const col = colOf(b) + 1;
  sys.water = {
    cols: new Array<number>(WATER_CELLS).fill(col), rows: new Array<number>(WATER_CELLS).fill(row),
    mode: -1, monsters: [], players: [],
  };
  return true;
}

/** Is a player being carried by the water? (The PLAYER stream may skip its own movement meanwhile.) */
export function carriedByWater(world: World, id: PlayerId): boolean {
  return systemOf(world).water?.players.includes(id) ?? false;
}

/**
 * A player in a jump - rising or coming down - is not caught, and leaves the stream when it jumps
 * (L_1BDE: D_87A0 >= 0; D_222B -> state 1). The contract has no jump flag: this reads the PLAYER
 * stream's `motion.jump`, its port of D_87A0.
 */
const inJump = (p: Player): boolean => p.motion.jump >= 0;

/** One I-tick of the stream: catch, shift, move the head, then carry (the sprites' state $0D). */
export function waterTick(world: World): void {
  const sys = systemOf(world);
  const w = sys.water;
  if (!w) return;
  catchAtCell9(world, w);
  for (let i = 0; i < WATER_CELLS - 1; i++) {
    w.cols[i] = w.cols[i + 1] ?? 0;
    w.rows[i] = w.rows[i + 1] ?? WATER_END_ROW;
  }
  const ended = w.rows[0] === WATER_END_ROW;
  if (!ended && (w.rows[WATER_CELLS - 1] ?? WATER_END_ROW) < WATER_END_ROW) moveHead(world, w);
  carry(world, w);
  if (ended) sys.water = null;
}

/** L_1BB9..L_1C11: cell 9, on screen, catches monsters (sprite states 1..$0A) and players not in a jump. */
function catchAtCell9(world: World, w: WaterState): void {
  const row = w.rows[WATER_CARRY_CELL] ?? WATER_END_ROW;
  if (row < 4 || row >= WATER_END_ROW) return;
  const hx = ((w.cols[WATER_CARRY_CELL] ?? 0) * 8 + 0x14) & 0xff;
  const hy = (row * 8 + WATER_Y_OFFSET) & 0xff;
  const near = (x: number, y: number): boolean => Math.abs(x - hx) < WATER_RANGE && Math.abs(y - hy) < WATER_RANGE;
  for (let i = world.monsters.length - 1; i >= 0; i--) {
    const m = world.monsters[i];
    if (!m || m.state === 'trapped' || m.state === 'dead' || !near(m.x, m.y)) continue;
    m.state = 'trapped';
    m.bubbleId = -1;
    w.monsters.push(m.id);
  }
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    // Sprite states below $0B only: not dying, not respawning ($10, invulnerable), not already carried.
    if (p.state !== 'play' || p.invulnerable > 0 || w.players.includes(id) || inJump(p)) continue;
    if (!near(p.x, p.y)) continue;
    w.players.push(id);
  }
}

/**
 * L_1C32..L_1C97, the head (cell 10) from the cell (row - 4, column - 1) of the map: falling, it keeps
 * falling while inside a platform or over space, and on landing picks a side at random; flowing, it
 * falls again over space, else moves along its side, turning back at a wall - and with walls on both
 * sides it sinks through the platform.
 */
function moveHead(world: World, w: WaterState): void {
  const h = WATER_CELLS - 1;
  const col = w.cols[h] ?? 0;
  const row = w.rows[h] ?? WATER_END_ROW;
  const solid = (dRow: number, dCol: number): boolean => mapSolid(world, col + dCol, row - 4 + dRow);
  const down = (): void => {
    w.rows[h] = row + 1;
  };
  if (w.mode < 0) {
    if (solid(0, 0) || !solid(1, 0)) return down();
    w.mode = (rngOf(world).int(256) & 1) | 4;
  }
  if (!solid(1, 0)) {
    w.mode = -1;
    return down();
  }
  if ((w.mode & 1) === 0) {
    if (!solid(0, -1)) {
      w.cols[h] = col - 1;
      return;
    }
    w.mode++;
  }
  if (!solid(0, 1)) {
    w.cols[h] = col + 1;
    return;
  }
  w.mode--;
  if (solid(0, -1)) down();
}

/**
 * State $0D (spawn_position_init): each carried sprite sits on cell 9. At the bottom (Y $ED) a monster
 * dies there and falls as a yellow diamond; a player is set down at X $64 (or $B4 right of $94) and
 * falls on. A player who has jumped (or stopped playing) is let go.
 */
function carry(world: World, w: WaterState): void {
  const x = ((w.cols[WATER_CARRY_CELL] ?? 0) * 8 + 0x14) & 0xff;
  const y = ((w.rows[WATER_CARRY_CELL] ?? WATER_END_ROW) * 8 + WATER_Y_OFFSET) & 0xff;
  const bottom = y === WATER_BOTTOM_Y;
  for (const id of w.monsters.slice()) {
    const m = world.monsters.find((o) => o.id === id);
    if (!m || m.state !== 'trapped' || m.bubbleId !== -1) {
      w.monsters.splice(w.monsters.indexOf(id), 1);
      continue;
    }
    m.x = x;
    m.y = y;
    if (!bottom) continue;
    w.monsters.splice(w.monsters.indexOf(id), 1);
    killMonster(world, m, DIED_IN_WATER, 'water');
  }
  for (const id of w.players.slice()) {
    const p = world.players[id];
    const letGo = (): void => {
      w.players.splice(w.players.indexOf(id), 1);
    };
    if (p.state !== 'play' || inJump(p)) {
      letGo();
      continue;
    }
    p.x = bottom ? WATER_DROP_X[x < WATER_DROP_SPLIT ? 0 : 1] : x;
    p.y = y;
    if (bottom) letGo();
  }
}
